import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { springPresets } from '@/lib/motion';
import { useLanguage } from '@/lib/language';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sekretna kombinacja klawiszy dla admin panelu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        navigate(ROUTE_PATHS.ADMIN);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);
  const navItems = [{
    to: ROUTE_PATHS.HOME,
    label: t('nav.home')
  }, {
    to: ROUTE_PATHS.TRACKING,
    label: t('nav.tracking')
  }, {
    to: ROUTE_PATHS.SERVICES,
    label: t('nav.services')
  }, {
    to: ROUTE_PATHS.ABOUT,
    label: t('nav.about')
  }, {
    to: ROUTE_PATHS.ORDER,
    label: t('nav.order')
  }, {
    to: ROUTE_PATHS.CONTACT,
    label: t('nav.contact')
  }];
  return <div className="min-h-screen flex flex-col">
      <motion.header ref={headerRef} initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={springPresets.gentle} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${scrolled ? 'bg-background/80 backdrop-blur-md shadow-lg border-b border-border' : 'bg-background/60 backdrop-blur-sm'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 group">
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} transition={springPresets.snappy} className="w-10 h-10 rounded-xl overflow-hidden shadow-lg">
                <img src={IMAGES.EXPRESS_HUB_LOGO} alt="EXPRESS HUB" className="w-full h-full object-contain" />
              </motion.div>
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                EXPRESS HUB
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map(item => <NavLink key={item.to} to={item.to} className={({
              isActive
            }) => `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive ? 'bg-primary text-primary-foreground shadow-md' : 'text-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                  {item.label}
                </NavLink>)}
            </nav>

            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="compact" className="hidden md:flex" />
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-accent transition-colors" aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
              <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
                {navItems.map(item => <NavLink key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)} className={({
              isActive
            }) => `px-4 py-3 rounded-lg font-medium transition-all duration-200 ${isActive ? 'bg-primary text-primary-foreground shadow-md' : 'text-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                    {item.label}
                  </NavLink>)}
                <div className="px-4 py-2 border-t border-border mt-2 pt-4">
                  <LanguageSwitcher variant="compact" />
                </div>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </motion.header>

      <main style={{
      paddingTop: `${headerHeight}px`
    }} className="flex-1">
        {children}
      </main>

      <footer className="bg-card border-t border-border mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl overflow-hidden shadow-lg">
                  <img src={IMAGES.EXPRESS_HUB_LOGO} alt="EXPRESS HUB" className="w-full h-full object-contain" />
                </div>
                <span className="text-lg font-bold">EXPRESS HUB</span>
              </div>
              <p className="text-muted-foreground text-sm">
                {t('footer.description')}
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.quickLinks')}</h3>
              <ul className="space-y-2">
                {navItems.map(item => <li key={item.to}>
                    <Link to={item.to} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.contact')}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Email: support@expresshub.pro</li>
                <li>{t('common.phone')} (Polska): +48 22 263 91 91</li>
                <li>{t('common.phone')} (Szwajcaria): +41 415 331 634</li>
                <li>{t('footer.hours')}: {t('workingHours.full')}</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>;
}